import React, { useMemo, useState } from 'react';
import { Dropdown, Slider, InputNumber, Tooltip } from 'antd';
import { ClockCircleOutlined, BgColorsOutlined, UploadOutlined, AppstoreOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useLayerProperitesContext } from '../../../../../context/LayerPropertiesContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MapIconsService, { RecentIconItem } from '../../../../../services/MapIconService/mapIcon.service';
import { QueryKeys } from '../../../../../enums/queryKeys';
import { BUILT_IN_SHAPES, isBuiltInShape, findBuiltInShape } from './builtInShapes';
import '../../LayerProperties.scss';
import './IconControls.scss';

// US-101662 Scenario 6: rotation must be between 0 and 360 degrees inclusive.
// Wording matches the AC exactly.
const ROTATION_ERROR = 'Rotation must be between 0 and 360 degrees';
const isValidRotation = (n: number | null | undefined) => n == null || (Number.isFinite(n) && n >= 0 && n <= 360);

interface IconControlsProps {
  typeKey: 'iconType' | 'uniqueIconType' | 'quantityIconType';
  sizeKey: 'iconSize' | 'uniqueIconSize' | 'quantityIconSize';
  rotationKey: 'iconRotation' | 'uniqueIconRotation' | 'quantityIconRotation';
  iconKey: 'selectedIcon' | 'uniqueIcon' | 'quantityIcon';
  onRequestIconSelector: (source: 'recent' | 'styles' | 'upload') => void;
}

const IconControls: React.FC<IconControlsProps> = ({ typeKey, sizeKey, rotationKey, iconKey, onRequestIconSelector }) => {
  const { setState, ...state } = useLayerProperitesContext();
  const stateRecord = state as unknown as Record<string, unknown>;
  const sizeValue = stateRecord[sizeKey] as number | undefined;
  const rotationValue = stateRecord[rotationKey] as number | undefined;
  const selectedIconValue = stateRecord[iconKey] as string | undefined;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [submenu, setSubmenu] = useState<'none' | 'recent' | 'custom'>('none');
  const { getRecentIcons, fetchIconUrl, touchRecentIcon } = MapIconsService();
  const { mutate: touchRecentIconMutate } = useMutation(touchRecentIcon());
  const queryClient = useQueryClient();

  // Resolve uploaded images to a presigned URL so the trigger shows the real
  // icon thumbnail rather than the literal word "Image".
  const isImageKey = !!selectedIconValue && !isBuiltInShape(selectedIconValue);
  const { data: triggerIconUrl } = useQuery(fetchIconUrl(isImageKey ? selectedIconValue : undefined));

  const triggerPreview = useMemo(() => {
    if (!selectedIconValue) return null;
    const shape = findBuiltInShape(selectedIconValue);
    if (shape) {
      return (
        <svg viewBox="0 0 24 24" className="icon-controls__trigger-svg" aria-hidden>
          <path d={shape.path} />
        </svg>
      );
    }
    if (triggerIconUrl) {
      return <img src={triggerIconUrl} className="icon-controls__trigger-img" alt="" />;
    }
    return <span className="icon-controls__trigger-label">Image</span>;
  }, [selectedIconValue, triggerIconUrl]);
  const { data: recentIcons } = useQuery({
    ...getRecentIcons(),
    enabled: dropdownOpen,
    staleTime: 60_000,
  });

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSubmenu('none');
  };

  const selectIcon = (next: string, kind: 'shape' | 'image') => {
    setState((prev) => ({
      ...prev,
      [iconKey]: next,
      // iconType is consumed by the SLD generator: "styles"/"upload" both
      // emit an <ExternalGraphic> (raster icon); anything else falls
      // through to a <Mark><WellKnownName> branch. So shape => "shape"
      // (any non-styles/non-upload value works), image => "upload".
      [typeKey]: kind === 'shape' ? 'shape' : 'upload',
      iconUploadKind: kind,
    }));
    // Record the pick so it shows up under "Recently used" (built-in shapes
    // and recent icons selected inline weren't being recorded before).
    touchRecentIconMutate({ key: next });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
    closeDropdown();
  };

  // US-101662 UX fix: submenus open on *click* only (no hover-open).
  // A back chevron in the submenu header returns to the top-level menu.
  const renderDropdownContent = () => {
    if (submenu === 'recent') {
      return (
        <div className="icon-controls__panel">
          <button type="button" className="icon-controls__panel-back" onClick={() => setSubmenu('none')}>
            <LeftOutlined />
            <span>Recently used</span>
          </button>
          <div className="icon-controls__grid">
            {(recentIcons ?? []).map((r: RecentIconItem) => (
              <button
                key={r.iconKey}
                type="button"
                className={`icon-controls__grid-item${selectedIconValue === r.iconKey ? ' icon-controls__grid-item--active' : ''}`}
                onClick={() => selectIcon(r.iconKey, isBuiltInShape(r.iconKey) ? 'shape' : 'image')}
                title={r.iconKey}
              >
                {isBuiltInShape(r.iconKey) ? (
                  <svg viewBox="0 0 24 24" className="icon-controls__grid-svg">
                    <path d={findBuiltInShape(r.iconKey)!.path} />
                  </svg>
                ) : (
                  <img src={r.url} alt="" className="icon-controls__grid-img" />
                )}
              </button>
            ))}
            {(!recentIcons || recentIcons.length === 0) && <div className="icon-controls__panel-empty">No recently used icons yet.</div>}
          </div>
        </div>
      );
    }
    if (submenu === 'custom') {
      return (
        <div className="icon-controls__panel">
          <button type="button" className="icon-controls__panel-back" onClick={() => setSubmenu('none')}>
            <LeftOutlined />
            <span>Custom icon</span>
          </button>
          <div className="icon-controls__grid">
            {BUILT_IN_SHAPES.map((shape) => (
              <button
                key={shape.key}
                type="button"
                className={`icon-controls__grid-item${selectedIconValue === shape.key ? ' icon-controls__grid-item--active' : ''}`}
                onClick={() => selectIcon(shape.key, 'shape')}
                title={shape.label}
              >
                <svg viewBox="0 0 24 24" className="icon-controls__grid-svg">
                  <path d={shape.path} />
                </svg>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="icon-controls__menu">
        <button type="button" className="icon-controls__menu-item" onClick={() => setSubmenu('recent')}>
          <ClockCircleOutlined />
          <span>Recently used</span>
          <RightOutlined className="icon-controls__menu-chevron" />
        </button>
        <button
          type="button"
          className="icon-controls__menu-item"
          onClick={() => {
            onRequestIconSelector('styles');
            closeDropdown();
          }}
        >
          <BgColorsOutlined />
          <span>Choose from styles</span>
        </button>
        <button
          type="button"
          className="icon-controls__menu-item"
          onClick={() => {
            onRequestIconSelector('upload');
            closeDropdown();
          }}
        >
          <UploadOutlined />
          <span>Upload</span>
        </button>
        <button type="button" className="icon-controls__menu-item" onClick={() => setSubmenu('custom')}>
          <AppstoreOutlined />
          <span>Custom icon</span>
          <RightOutlined className="icon-controls__menu-chevron" />
        </button>
      </div>
    );
  };

  const rotationInvalid = !isValidRotation(rotationValue);

  return (
    <>
      {/* Icon dropdown trigger — label stacked above the input, matching Figma */}
      <div className="icon-controls__group">
        <div className="icon-controls__group-label">ICON</div>
        <Dropdown
          trigger={['click']}
          open={dropdownOpen}
          onOpenChange={(next) => {
            setDropdownOpen(next);
            if (!next) setSubmenu('none');
          }}
          placement="bottomLeft"
          dropdownRender={() => renderDropdownContent()}
        >
          <button type="button" className="icon-controls__trigger">
            <span className="icon-controls__trigger-preview">{triggerPreview}</span>
            <RightOutlined rotate={90} className="icon-controls__trigger-caret" />
          </button>
        </Dropdown>
      </div>

      {/* Icon Size */}
      <div className="icon-controls__group">
        <div className="icon-controls__group-label">SIZE</div>
        <div className="icon-controls__group-row">
          <Slider
            className="icon-controls__slider"
            value={sizeValue}
            min={0}
            max={100}
            onChange={(value) => setState((prev) => ({ ...prev, [sizeKey]: value }))}
          />
          <InputNumber
            className="icon-controls__num-input"
            value={sizeValue}
            min={0}
            max={100}
            onChange={(value) =>
              setState((prev) => ({
                ...prev,
                [sizeKey]: typeof value === 'number' ? value : 0,
              }))
            }
            formatter={(v) => (v == null ? '' : `${v}pt`)}
            parser={(v) => (v ? Number(String(v).replace('pt', '')) : 0) as 0}
          />
        </div>
      </div>

      {/* Icon Rotation */}
      <div className="icon-controls__group">
        <div className="icon-controls__group-label">ICON ROTATION</div>
        <div className="icon-controls__group-row">
          <Slider
            className="icon-controls__slider"
            min={0}
            max={360}
            value={rotationValue}
            onChange={(value) => setState((prev) => ({ ...prev, [rotationKey]: value }))}
          />
          <Tooltip title={rotationInvalid ? ROTATION_ERROR : undefined} open={rotationInvalid ? true : false}>
            <InputNumber
              className="icon-controls__num-input"
              value={rotationValue}
              min={0}
              max={360}
              status={rotationInvalid ? 'error' : undefined}
              onChange={(value) =>
                setState((prev) => ({
                  ...prev,
                  [rotationKey]: typeof value === 'number' ? value : 0,
                }))
              }
              formatter={(v) => (v == null ? '' : `${v}°`)}
              parser={(v) => (v ? Number(String(v).replace('°', '')) : 0) as 0}
              placeholder="°"
            />
          </Tooltip>
        </div>
        {rotationInvalid && <div className="icon-controls__rotation-error">{ROTATION_ERROR}</div>}
      </div>
    </>
  );
};

export default IconControls;
